import React, { useState, useMemo } from 'react';
import APIUtils from '../../utils/APIUtils';
import InterpreterAutocomplete from '../../uiComponents/MuiAutocomplete/InterpreterAutocomplete';
import { AddCircleOutline } from '@mui/icons-material';

const ASSIGNED_INTERPRETERS_ERROR = "Assigned interpreters exceeds the number of required interpretations";
const DUPLICATE_INTERPRETERS_ERROR = "Please remove duplicate interpreters.";

// Encapsulates all Molecular-tab state, dirty tracking, interpreter combobox
// handlers, and the save/cancel flow. The save payload also carries the current
// biochemical settings to avoid nulling them out on the shared program-settings
// endpoint, so those read-only values are injected. The setters are exposed so
// the page-load data hook can hydrate this state.
const useMolecularSettings = ({
  programId,
  biochemicalInstrumentation,
  selectedBiochemicalValues,
  cutoffSettingsSelection,
  CUTOFF_MANUAL_OPTION,
  showSnackbar,
}) => {
  const [initialInterpretationCount, setInitialInterpretationCount] = useState(3);
  const [defaultInterpretationCount, setDefaultInterpretationCount] = useState(3);
  const [reviewThreshold, setReviewThreshold] = useState('');
  const [defaultHgvsRef, setDefaultHgvsRef] = useState('');
  const [initialInterpreters, setInitialInterpreters] = useState([]);
  const [defaultInterpreters, setDefaultInterpreters] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [comboboxErrors, setComboboxErrors] = useState({});
  const [inputValues, setInputValues] = useState({});
  const [molecularError, setMolecularError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [initialReviewThreshold, setInitialReviewThreshold] = useState('');
  const [initialDefaultHgvsRef, setInitialDefaultHgvsRef] = useState('');
  const [molecularSaving, setMolecularSaving] = useState(false);

  const molecularDirty = useMemo(() => {
    if (defaultInterpretationCount !== initialInterpretationCount) return true;
    if (reviewThreshold !== initialReviewThreshold) return true;
    if (defaultHgvsRef !== initialDefaultHgvsRef) return true;
    const cleanedCurrent = defaultInterpreters.filter(u => u && u.trim() !== '' && u !== '__new__');
    if (cleanedCurrent.length !== initialInterpreters.length) return true;
    return cleanedCurrent.some((email, i) => email !== initialInterpreters[i]);
  }, [defaultInterpretationCount, initialInterpretationCount, reviewThreshold, initialReviewThreshold, defaultHgvsRef, initialDefaultHgvsRef, defaultInterpreters, initialInterpreters]);

  const handleInterpretationCountChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setDefaultInterpretationCount(value);
    if (defaultInterpreters.length <= value) {
      setValidationErrors(prev => prev.filter(err => err !== ASSIGNED_INTERPRETERS_ERROR));
    }
  };

  const handleComboboxChange = (index, selectedUser) => {
    const updated = [...defaultInterpreters];
    updated[index] = selectedUser?.email || '';
    setDefaultInterpreters(updated);
    setInputValues(prev => ({ ...prev, [index]: selectedUser?.fullName || '' }));
  };

  const handleInputChange = (index, newInputValue) => {
    setInputValues(prev => ({ ...prev, [index]: newInputValue }));
  };

  const handleDeleteInterpreter = (index) => {
    const updated = defaultInterpreters.filter((_, i) => i !== index);
    const updatedComboboxErrors = { ...comboboxErrors };
    delete updatedComboboxErrors[index];

    const shiftedErrors = {};
    Object.keys(updatedComboboxErrors).forEach(key => {
      const keyNum = parseInt(key, 10);
      if (keyNum > index) shiftedErrors[keyNum - 1] = updatedComboboxErrors[key];
      else if (keyNum < index) shiftedErrors[keyNum] = updatedComboboxErrors[key];
    });

    const updatedInputs = {};
    Object.keys(inputValues).forEach(key => {
      const keyNum = parseInt(key, 10);
      if (keyNum < index) updatedInputs[keyNum] = inputValues[keyNum];
      else if (keyNum > index) updatedInputs[keyNum - 1] = inputValues[keyNum];
    });

    const unique = [...new Set(updated)];
    let newErrors = validationErrors.filter(
      err => err !== ASSIGNED_INTERPRETERS_ERROR && err !== DUPLICATE_INTERPRETERS_ERROR
    );
    if (updated.length > defaultInterpretationCount) newErrors.push(ASSIGNED_INTERPRETERS_ERROR);
    if (updated.length !== unique.length) newErrors.push(DUPLICATE_INTERPRETERS_ERROR);

    setDefaultInterpreters(updated);
    setInputValues(updatedInputs);
    setComboboxErrors(shiftedErrors);
    setValidationErrors(newErrors);
  };

  const handleAddInterpreter = () => {
    setDefaultInterpreters(prev => [...prev, '__new__']);
    setInputValues(prev => ({ ...prev, [Object.keys(prev).length]: '' }));
  };

  const handleCancelMolecular = () => {
    setDefaultInterpretationCount(initialInterpretationCount);
    setReviewThreshold(initialReviewThreshold);
    setDefaultHgvsRef(initialDefaultHgvsRef);
    setDefaultInterpreters([...initialInterpreters]);
    // Rebuild inputValues from initial interpreters
    const restoredInputVals = {};
    initialInterpreters.forEach((email, idx) => {
      const user = availableUsers.find(u => u.email === email);
      restoredInputVals[idx] = user?.fullName || '';
    });
    setInputValues(restoredInputVals);
    setComboboxErrors({});
    setValidationErrors([]);
    setMolecularError('');
  };

  const onSubmitMolecular = async () => {
    const cleanedUsers = defaultInterpreters.filter(user => user && user.trim() !== '' && user !== '__new__');
    const uniqueUsers = [...new Set(cleanedUsers)];
    let newErrors = [];
    let newComboboxErrors = {};

    cleanedUsers.forEach((email, index) => {
      const found = availableUsers.some(user => user.email === email);
      if (!found) newComboboxErrors[index] = 'Please select a valid interpreter.';
    });

    if (cleanedUsers.length > defaultInterpretationCount) newErrors.push(ASSIGNED_INTERPRETERS_ERROR);
    if (cleanedUsers.length !== uniqueUsers.length) newErrors.push(DUPLICATE_INTERPRETERS_ERROR);

    setComboboxErrors(newComboboxErrors);
    setValidationErrors(newErrors);

    if (newErrors.length > 0 || Object.keys(newComboboxErrors).length > 0) return;

    const visibleUser = cleanedUsers.slice(0, defaultInterpretationCount);

    // Include biochemical data to avoid nulling it out
    const mergedBiochemicalInstrumentation = biochemicalInstrumentation.map(row => ({
      ...row,
      ...(selectedBiochemicalValues[row.AnalyteGroup] || {}),
    }));

    const payload = {
      reviewThreshold,
      defaultInterpretationCount,
      defaultInterpreters: visibleUser,
      defaultHgvsRef,
      biochemicalInstrumentationSettings: mergedBiochemicalInstrumentation,
      hasManualCutoff: cutoffSettingsSelection === CUTOFF_MANUAL_OPTION,
    };

    try {
      setMolecularSaving(true);
      setMolecularError('');
      await APIUtils.updateProgramSettings(programId, payload);
      setInitialInterpreters([...visibleUser]);
      setInitialInterpretationCount(defaultInterpretationCount);
      setInitialReviewThreshold(reviewThreshold);
      setInitialDefaultHgvsRef(defaultHgvsRef);
      showSnackbar('Molecular settings saved successfully.');
    } catch (err) {
      console.error('API error:', err);
      let userMessage = 'An error occurred while saving molecular settings. Please try again.';
      if (err.response && err.response.status === 415) {
        userMessage = 'The server could not process your request. Please contact ED3N support or try again later.';
      } else if (err.response?.data?.message) {
        userMessage = err.response.data.message;
      }
      setMolecularError(userMessage);
    } finally {
      setMolecularSaving(false);
    }
  };

  const renderComboboxes = () => (
    <>
      {defaultInterpreters.map((email, i) => {
        const isSavedInterpreter = initialInterpreters.includes(email) && email !== '__new__';
        const comboboxDisabled = isSavedInterpreter;
        const userObj = availableUsers.find(user => user.email === email);
        const fullName = userObj?.fullName || '';
        const labelText = comboboxDisabled ? 'Interpreter (Currently Assigned)' : 'Interpreter (New)';

        return (
          <InterpreterAutocomplete
            key={i}
            index={i}
            value={availableUsers.find(user => user.fullName === (inputValues[i] ?? fullName)) || null}
            inputValue={inputValues[i] ?? fullName}
            options={availableUsers}
            disabled={comboboxDisabled}
            label={labelText}
            onSelectionChange={handleComboboxChange}
            onInputChange={handleInputChange}
            onRemove={handleDeleteInterpreter}
            error={comboboxErrors[i]}
            ariaLabel={`interpreter_${i + 1}_input`}
          />
        );
      })}
      {defaultInterpreters.length < 5 && (
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '0.5rem' }}>
          <button onClick={handleAddInterpreter} className="addRemoveButton">
            <span className="addRemoveButtonSpan">
              <AddCircleOutline className="addRemoveIcons" alt="add" fontSize="small" />
            </span>
            <span>Add</span>
          </button>
        </div>
      )}
    </>
  );

  return {
    // State
    defaultInterpretationCount,
    reviewThreshold,
    defaultHgvsRef,
    defaultInterpreters,
    availableUsers,
    molecularError,
    validationErrors,
    molecularSaving,
    molecularDirty,
    // Setters (consumed by useProgramSettingsData and tab bindings)
    setReviewThreshold,
    setDefaultHgvsRef,
    setInitialReviewThreshold,
    setInitialDefaultHgvsRef,
    setInitialInterpretationCount,
    setDefaultInterpretationCount,
    setInitialInterpreters,
    setDefaultInterpreters,
    setAvailableUsers,
    setInputValues,
    setMolecularError,
    // Handlers
    handleInterpretationCountChange,
    handleAddInterpreter,
    handleCancelMolecular,
    onSubmitMolecular,
    renderComboboxes,
  };
};

export default useMolecularSettings;
